'use client'

import Image from 'next/image'
import Link from 'next/link'
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Check,
  ChevronDown,
  Download,
  GraduationCap,
  Menu,
  Play,
  School,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import styles from './landing.module.css'

const graduateSignup = 'https://app.intrns.com/signup/graduate-intern'
const companySignup = 'https://app.intrns.com/signup/company'

const paths = [
  {
    id: 'students',
    title: 'Students',
    icon: GraduationCap,
    theme: styles.student,
    description:
      'Add your academic, professional and personal information so we can match you with the right opportunities.',
    action: 'Explore Opportunities',
    href: '/school-portals',
    benefits: [
      'Discover verified internship opportunities',
      'Gain hands-on work experience',
      'Build your CV and career network',
    ],
  },
  {
    id: 'graduates',
    title: 'Graduates & Interns',
    icon: UserRound,
    theme: styles.graduate,
    description: 'Discover opportunities, build your skills and grow your career beyond school.',
    action: 'Find Your Next Step',
    href: graduateSignup,
    benefits: [
      'Explore jobs and early-career roles',
      'Develop in-demand skills',
      'Connect with top employers',
    ],
  },
  {
    id: 'companies',
    title: 'Companies',
    icon: Building2,
    theme: styles.company,
    description: 'Find and hire talent and early-career professionals for your team.',
    action: 'Hire Talent',
    href: companySignup,
    benefits: [
      'Find and hire pre-vetted talent',
      'Simplify your recruitment',
      'Build a strong future workforce',
    ],
  },
]

const platformFeatures = [
  {
    title: 'Internship Opportunities',
    icon: BriefcaseBusiness,
    description: 'Browse verified internships and early-career roles from trusted companies.',
  },
  {
    title: 'Talent Profiles & Matching',
    icon: UsersRound,
    description: 'Discover skilled students, graduates and interns ready to contribute.',
  },
  {
    title: 'Employer View',
    icon: Building2,
    description: 'Manage applications, shortlist, talent and build your team.',
  },
]

const audiences = [
  { label: 'Students', icon: GraduationCap, href: '/school-portals' },
  { label: 'Graduates', icon: UsersRound, href: graduateSignup },
  { label: 'Employers', icon: BriefcaseBusiness, href: companySignup },
  { label: 'Universities', icon: School, href: '/school-portals' },
]

const socials = [
  { name: 'Facebook', image: 'facebook.png', href: 'https://www.facebook.com/koonageinfotec/' },
  { name: 'Instagram', image: 'instagram.png', href: 'https://www.instagram.com/koonageinfotec/' },
  { name: 'X', image: 'x.png', href: 'https://www.x.com/koonageinfotec/' },
  { name: 'LinkedIn', image: 'linkedIn.png', href: 'https://www.linkedin.com/company/106294740/' },
]

function Logo() {
  return (
    <Image
      src="/images/landing/logo.png"
      alt="Intrn"
      width={120}
      height={52}
      className={styles.logo}
    />
  )
}

function ActionLink({
  href,
  children,
  className = '',
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a href={href} className={`${styles.actionLink} ${className}`}>
      {children}
      <ArrowRight aria-hidden="true" />
    </a>
  )
}

function TestimonyVideo() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [started, setStarted] = useState(false)
  const [error, setError] = useState(false)

  async function playVideo() {
    setError(false)
    setStarted(true)
    try {
      await videoRef.current?.play()
    } catch {
      setStarted(false)
      setError(true)
    }
  }

  return (
    <div className={styles.videoFrame}>
      <div className={styles.videoCard}>
        <video
          ref={videoRef}
          src="/videos/video-2.mp4"
          poster="/images/landing/vivian-testimony-poster.jpg"
          className={started ? styles.videoPlaying : undefined}
          preload="none"
          playsInline
          controls={started}
          aria-label="Student testimonial from Vivian Asogwa"
          onPlay={() => setStarted(true)}
          onEnded={() => {
            setStarted(false)
            if (videoRef.current) videoRef.current.currentTime = 0
          }}
        />
        {!started && (
          <>
            <div className={styles.videoShade} />
            <button
              className={styles.playButton}
              type="button"
              aria-label="Play student testimonial"
              onClick={playVideo}
            >
              <Play aria-hidden="true" fill="currentColor" />
            </button>
            <div className={styles.videoCaption}>
              <strong>Vivian Asogwa</strong>
              <span>Intern, Koonage</span>
            </div>
          </>
        )}
      </div>
      {error && (
        <p role="alert" className={styles.videoError}>
          The video could not start. <a href="/videos/video-2.mp4">Open the video</a>.
        </p>
      )}
    </div>
  )
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [signupOpen, setSignupOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const menuRef = useRef<HTMLButtonElement>(null)
  const signupRef = useRef<HTMLDivElement>(null)
  const signupButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!menuOpen && !signupOpen) return
    function closeOutside(event: PointerEvent) {
      if (!headerRef.current?.contains(event.target as Node)) setMenuOpen(false)
      if (!signupRef.current?.contains(event.target as Node)) setSignupOpen(false)
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (signupOpen) {
          setSignupOpen(false)
          signupButtonRef.current?.focus()
        } else {
          setMenuOpen(false)
          menuRef.current?.focus()
        }
      }
    }
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [menuOpen, signupOpen])

  return (
    <main className={styles.landing}>
      <a href="#paths" className={styles.skipLink}>
        Skip to opportunities
      </a>
      <header className={styles.header} ref={headerRef}>
        <Link href="/" aria-label="Intrn home">
          <Logo />
        </Link>
        <button
          ref={menuRef}
          type="button"
          className={styles.menuButton}
          aria-expanded={menuOpen}
          aria-controls="landing-nav"
          aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
          onClick={() => {
            setMenuOpen(!menuOpen)
            setSignupOpen(false)
          }}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        <div
          id="landing-nav"
          className={`${styles.headerMenu} ${menuOpen ? styles.navigationOpen : ''}`}
        >
          <nav aria-label="Main navigation" className={styles.navigation}>
            <Link href="/school-portals" onClick={() => setMenuOpen(false)}>
              School Portals
            </Link>
            <a href={companySignup}>Companies</a>
            <Link href="/about-us" onClick={() => setMenuOpen(false)}>
              About Us
            </Link>
          </nav>
          <div
            className={styles.signupWrapper}
            ref={signupRef}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setSignupOpen(false)
            }}
          >
            <button
              ref={signupButtonRef}
              type="button"
              className={styles.signup}
              aria-expanded={signupOpen}
              aria-controls="signup-personas"
              onClick={() => {
                setSignupOpen(!signupOpen)
              }}
            >
              Sign Up <ChevronDown aria-hidden="true" />
            </button>
            {signupOpen && (
              <nav
                id="signup-personas"
                className={styles.signupDropdown}
                aria-label="Signup personas"
              >
                <a
                  href="https://app.intrns.com/signup/student"
                  onClick={() => {
                    setSignupOpen(false)
                    setMenuOpen(false)
                  }}
                >
                  <GraduationCap aria-hidden="true" />
                  Sign up as Student
                </a>
                <a
                  href={companySignup}
                  onClick={() => {
                    setSignupOpen(false)
                    setMenuOpen(false)
                  }}
                >
                  <Building2 aria-hidden="true" />
                  Sign up as Company
                </a>
                <Link
                  href="/school-portals"
                  onClick={() => {
                    setSignupOpen(false)
                    setMenuOpen(false)
                  }}
                >
                  <School aria-hidden="true" />
                  Sign up as School
                </Link>
              </nav>
            )}
          </div>
        </div>
      </header>

      <section className={styles.hero} aria-labelledby="hero-title">
        <div className={styles.heroIntro}>
          <Image
            src="/images/landing/hero.png"
            alt="A student, graduate and business professional ready for their next opportunity"
            width={1724}
            height={912}
            priority
            quality={95}
            sizes="(max-width: 800px) 100vw, 45vw"
            className={styles.heroImage}
          />
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>INTERNSHIPS. EARLY CAREERS. GLOBAL OPPORTUNITIES.</p>
            <h1 id="hero-title">
              One Platform.
              <br />
              Three Paths.
              <br />
              <span>Your Next Opportunity.</span>
            </h1>
            <p className={styles.heroDescription}>
              INTRN connects students, graduates and early career talents with meaningful
              opportunities - and helps companies find the right people, faster.
            </p>
          </div>
        </div>
        <div id="paths" className={styles.pathGrid}>
          {paths.map(({ id, title, icon: Icon, theme, description, action, href }) => (
            <article id={id} key={id} className={`${styles.pathCard} ${theme}`}>
              <div className={styles.pathContent}>
                <span className={styles.pathIcon}>
                  <Icon aria-hidden="true" />
                </span>
                <div>
                  <h2>{title}</h2>
                  <p>{description}</p>
                </div>
              </div>
              <ActionLink href={href}>{action}</ActionLink>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.why} aria-labelledby="why-title">
        <div className={styles.whyHeading}>
          <span className={styles.pill}>WHY INTRN</span>
          <h2 id="why-title">Built For Everyone on The Internship Journey</h2>
        </div>
        <div className={styles.benefitGrid}>
          {paths.map(({ id, title, icon: Icon, theme, benefits }) => (
            <article key={id} className={`${styles.benefitCard} ${theme}`}>
              <span className={styles.benefitIcon}>
                <Icon aria-hidden="true" />
              </span>
              <h3>{title}</h3>
              <ul>
                {benefits.map((benefit) => (
                  <li key={benefit}>
                    <Check aria-hidden="true" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.stories} aria-labelledby="stories-title">
        <div className={styles.storyGrid}>
          <TestimonyVideo />
          <div className={styles.storyDetails}>
            <div className={styles.storyQuote}>
              <h2 id="stories-title" className={styles.eyebrow}>
                STUDENT STORIES
              </h2>
              <blockquote>
                <span className={styles.quoteMark} aria-hidden="true">
                  ❝
                </span>
                <p>
                  &quot;My internship experience with Intrn opened doors I never thought possible.
                  The guidance and opportunities were invaluable in shaping my career path.&quot;
                </p>
              </blockquote>
              <div className={styles.stars} aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }, (_, index) => (
                  <Image key={index} src="/icons/star.svg" alt="" width={21} height={20} />
                ))}
              </div>
              <div className={styles.brandLogos}>
                <Logo />
                <span aria-hidden="true" />
                <Image src="/images/koonageLogo.png" alt="Koonage" width={133} height={32} />
              </div>
            </div>
            <div className={styles.guide}>
              <a
                href="https://youtu.be/l5WnfNfe840?si=6aJqYteCd1vuEsR7"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch how INTRN works on YouTube (opens in a new tab)"
                className={styles.guideVideo}
              >
                <Image
                  src="/images/landing/user-guide.png"
                  alt="Watch How INTRN Works"
                  width={528}
                  height={200}
                  sizes="(max-width: 800px) 100vw, 37vw"
                  unoptimized
                />
              </a>
              <a href="/guide.pdf" download="Intrn-Usage-Manual.pdf" className={styles.manual}>
                <Download aria-hidden="true" />
                Download Usage Manual
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.platform} aria-labelledby="platform-title">
        <div className={styles.platformCopy}>
          <div className={styles.platformHeading}>
            <p className={styles.eyebrow}>THE INTRN PLATFORM</p>
            <h2 id="platform-title">Opportunities. Talent. Connection.</h2>
            <p>
              Everything you need to find the opportunities or the right talent, all in one place.
            </p>
          </div>
          <div className={styles.platformFeatures}>
            {platformFeatures.map(({ title, icon: Icon, description }) => (
              <article key={title}>
                <span className={styles.platformIcon}>
                  <Icon aria-hidden="true" />
                </span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </div>
        <Image
          src="/images/landing/intrn-platform.png"
          alt="Intrn platform shown on desktop and mobile, including internship listings and applications"
          width={519}
          height={381}
          sizes="(max-width: 800px) 100vw, 37vw"
          unoptimized
          className={styles.platformImage}
        />
      </section>

      <section className={styles.closingCtas} aria-label="Take your next step with Intrn">
        <article className={`${styles.closingCard} ${styles.talentCard}`}>
          <div className={styles.closingCopy}>
            <h2>For Talent</h2>
            <p>Take the next step in your career with real opportunities and support.</p>
            <ActionLink href={graduateSignup}>Explore opportunities</ActionLink>
          </div>
          <Image
            src="/images/landing/talent.png"
            alt="Student holding a laptop"
            width={167}
            height={216}
            unoptimized
            className={styles.talentImage}
          />
        </article>
        <article className={`${styles.closingCard} ${styles.employerCard}`}>
          <div className={styles.closingCopy}>
            <h2>For Employers</h2>
            <p>Find and hire the right talent to move your business forward.</p>
            <ActionLink href={companySignup}>Hire Talent</ActionLink>
          </div>
          <Image
            src="/images/landing/employer.png"
            alt="Employer using a tablet"
            width={180}
            height={216}
            unoptimized
            className={styles.employerImage}
          />
        </article>
      </section>

      <section className={styles.audiences} aria-labelledby="audiences-title">
        <h2 id="audiences-title" className={styles.eyebrow}>
          A PLATFORM FOR EVERYONE
        </h2>
        <div className={styles.audienceGrid}>
          {audiences.map(({ label, icon: Icon, href }) => (
            <a key={label} href={href}>
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </a>
          ))}
        </div>
      </section>

      <footer className={styles.footer}>
        <Link href="/" aria-label="Intrn home">
          <Logo />
        </Link>
        <div className={styles.footerCredits}>
          <p>© 2025 Intrns.com All rights reserved.</p>
          <div className={styles.policyLabels}>
            <span>Privacy Policy</span>
            <span>Terms of Use</span>
            <span>Cookie Policy</span>
          </div>
        </div>
        <nav className={styles.socialLinks} aria-label="Social media">
          {socials.map(({ name, image, href }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${name} (opens in a new tab)`}
            >
              <Image src={`/icons/${image}`} alt="" width={30} height={30} />
            </a>
          ))}
        </nav>
      </footer>
    </main>
  )
}
